import { useEffect, useMemo, useState } from 'react';

import { CANDIDATE_LEVELS, fetchLocalCandidates } from '../utils/localCandidatesApi';
import {
  candidateMatchesRef,
  countyQueryFromRef,
  decodeLocalCandidateRef,
} from '../utils/localCandidateRef';

/**
 * Resolves one local candidate from a URL ref.
 *
 * The same shape as useCandidate, for the same reason: /elections/candidates
 * filters by state, level and county but has no by-id parameter, so there is no
 * direct lookup. That hook parses office and state out of an FEC ID, fetches
 * the one list they imply and finds the row in it. This does the same with the
 * row id the ref carries.
 *
 * The county in the ref is a narrowing hint, not identity. Indiana files 8,670
 * non-federal candidates, so a state-wide fetch is cut long before an arbitrary
 * row appears in it, and the county filter is what makes the list small enough
 * to contain the answer.
 *
 * Two passes. The first is county-filtered. If the row is not in it, the second
 * drops the filter -- a state can publish "Wayne" one week and "Wayne County"
 * the next, and losing somebody's page over a changed suffix is worse than one
 * extra request. Both passes match on the id, so a widened search cannot return
 * the wrong person. The second is skipped where it would repeat the first.
 *
 * @param {string} stateAbbr
 * @param {string} level - 'state' | 'county' | 'city' | 'local'
 * @param {string} ref
 */

// Higher than the list's 200. The list is something a person reads, so a cut
// list is a display problem; this is a lookup, and a cut list is a candidate
// who cannot be reached by their own URL.
//
// 500 rather than 1000: the endpoint rejects a limit past its cap with a 422,
// and the guide's own examples never go above 500. A rejected request looks
// exactly like a candidate who is not there, which is the worst way for a cap
// to announce itself.
const LOOKUP_LIMIT = 500;

const useLocalCandidate = (stateAbbr, level, ref) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(Boolean(stateAbbr && ref));
  const [error, setError] = useState(null);

  const parts = useMemo(() => decodeLocalCandidateRef(ref), [ref]);

  useEffect(() => {
    if (!stateAbbr || !ref) {
      setCandidate(null);
      setLoading(false);
      return undefined;
    }

    if (!parts) {
      setCandidate(null);
      setError(new Error('Not a valid candidate reference'));
      setLoading(false);
      return undefined;
    }

    if (!CANDIDATE_LEVELS.includes(level)) {
      setCandidate(null);
      setError(new Error(`Unknown level ${level}`));
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const county = countyQueryFromRef(parts);

        const first = await fetchLocalCandidates(
          { stateAbbr, level, county, limit: LOOKUP_LIMIT },
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;

        const exact = first.candidates.find((row) => candidateMatchesRef(row, parts));
        if (exact) {
          setCandidate(exact);
          return;
        }

        // Nothing to gain from repeating the identical request.
        if (!county) {
          setCandidate(null);
          setError(new Error('Candidate not found'));
          return;
        }

        const second = await fetchLocalCandidates(
          { stateAbbr, level, limit: LOOKUP_LIMIT },
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;

        const widened = second.candidates.find((row) => candidateMatchesRef(row, parts));

        if (widened) {
          setCandidate(widened);
          return;
        }

        setCandidate(null);
        // Said plainly rather than as a 404. A filing that was published last
        // month and is not published today is a real thing that happens, and
        // the page can say so.
        //
        // Flagged so the page can tell this apart from a request that failed.
        // The two mean opposite things -- one is the data, one is us -- and
        // showing the same sentence for both sends a reader looking for a
        // candidate who is sitting right there.
        const missing = new Error('Candidate not found');
        missing.notFound = true;
        setError(missing);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[useLocalCandidate] lookup failed', {
          stateAbbr,
          level,
          ref,
          message: err?.message,
          status: err?.status,
          query: err?.query,
        });
        setCandidate(null);
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [stateAbbr, level, ref, parts]);

  return { candidate, loading, error };
};

export default useLocalCandidate;